import {DOCUMENT} from '@angular/common';
import {ChangeDetectionStrategy, Component, DestroyRef, computed, inject, input, output, signal} from '@angular/core';
import {takeUntilDestroyed, toSignal} from '@angular/core/rxjs-interop';
import {NavigationEnd, Router, RouterLink, RouterLinkActive} from '@angular/router';
import {filter, map} from 'rxjs';
import {MenuItem} from 'primeng/api';
import {MenuModule} from 'primeng/menu';

import {AuthStore, ROLE_LABEL, UserRole} from '@core/auth';
import {ProfileForm} from '@core/auth/components/profile-form';
import {OrdersViewStore} from '@core/orders-view';
import {APP_ROUTES} from '@core/routes';
import {TenantForm} from '@core/tenant/components/tenant-form';
import {Avatar} from '@shared/ui/avatar';
import {Logo} from '@shared/ui/logo';
import {DrawerService} from '@shared/ui/drawer';
import {LanguageStore} from '@core/i18n';
import {TLabel, TranslateHelper, TranslatePipe} from '@mydeals/i18n';

const TABLET_MEDIA_QUERY = '(min-width: 768px) and (max-width: 1024px)';

interface NavChild {
  readonly label: TLabel;
  readonly route: string;
}

interface NavItem {
  readonly label: TLabel;
  readonly icon: string;
  readonly route: string;
  readonly children?: readonly NavChild[];
}

@Component({
  selector: 'crm-sidebar',
  imports: [RouterLink, RouterLinkActive, TranslatePipe, Avatar, Logo, MenuModule],
  templateUrl: './sidebar.html',
  styleUrl: './sidebar.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.is-pinned]': 'pinned()',
    '[class.is-expanded]': 'isExpanded()',
    '[class.is-tablet]': 'isTablet()',
    '(mouseenter)': 'onMouseEnter()',
    '(mouseleave)': 'onMouseLeave()',
  },
})
export class Sidebar {
  private readonly authStore = inject(AuthStore);
  private readonly document = inject(DOCUMENT);
  private readonly drawer = inject(DrawerService);
  private readonly router = inject(Router);
  private readonly translateHelper = inject(TranslateHelper);
  private readonly languageStore = inject(LanguageStore);
  private readonly ordersView = inject(OrdersViewStore);
  private readonly destroyRef = inject(DestroyRef);

  readonly pinned = input.required<boolean>();
  readonly togglePin = output<void>();

  private readonly isHover = signal(false);
  private readonly _isTablet = signal(false);
  readonly isTablet = this._isTablet.asReadonly();
  readonly isExpanded = computed(() => this.pinned() || (!this.isTablet() && this.isHover()));

  private readonly url = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map((e) => e.urlAfterRedirects),
    ),
    {initialValue: this.router.url},
  );

  constructor() {
    const mql = this.document.defaultView?.matchMedia(TABLET_MEDIA_QUERY);
    if (mql) {
      this._isTablet.set(mql.matches);
      const handler = (e: MediaQueryListEvent): void => this._isTablet.set(e.matches);
      mql.addEventListener('change', handler);
      this.destroyRef.onDestroy(() => mql.removeEventListener('change', handler));
    }
  }

  private readonly isAdmin = computed(() => this.authStore.currentUser()?.role === UserRole.Admin);

  readonly menuItems = computed<MenuItem[]>(() => {
    this.languageStore.current();
    const items: MenuItem[] = [];
    if (this.isAdmin()) {
      items.push({
        label: this.translateHelper.translate('tenant.title'),
        icon: 'pi pi-building',
        command: () => this.openTenant(),
      });
    }
    items.push({
      label: this.translateHelper.translate('profile.title'),
      icon: 'pi pi-user',
      command: () => this.openProfile(),
    });
    items.push({
      label: this.translateHelper.translate('common.logout'),
      icon: 'pi pi-sign-out',
      command: () => this.logout(),
    });
    return items;
  });

  private readonly settings = APP_ROUTES.settings;
  private readonly personnelRoute = '/' + APP_ROUTES.personnel.root;

  readonly navItems = computed<readonly NavItem[]>(() => {
    const items: NavItem[] = [
      {label: 'nav.orders', icon: 'pi pi-calendar-plus', route: this.ordersView.path()},
      {label: 'nav.customers', icon: 'pi pi-users', route: '/' + APP_ROUTES.customers},
      {label: 'nav.personnel', icon: 'pi pi-user-plus', route: this.personnelRoute},
      {
        label: 'nav.settings',
        icon: 'pi pi-cog',
        route: '/' + this.settings.root,
        children: [
          {label: 'nav.labels', route: `/${this.settings.root}/${this.settings.labels}`},
          {label: 'nav.cancellationReasons', route: `/${this.settings.root}/${this.settings.cancellationReasons}`},
        ],
      },
    ];

    if (!this.isAdmin()) {
      return items.filter((item) => item.route !== this.personnelRoute);
    }
    return items;
  });

  isGroupActive(item: NavItem): boolean {
    if (!item.children) return false;
    const current = this.url();
    return item.children.some((child) => current.startsWith(child.route));
  }

  readonly avatarUrl = computed(() => this.authStore.currentUser()?.avatarUrl ?? null);

  readonly fullName = computed(() => {
    const user = this.authStore.currentUser();
    if (!user) return '';
    return `${user.firstName} ${user.lastName}`.trim();
  });

  readonly roleLabel = computed<TLabel | null>(() => {
    const role = this.authStore.currentUser()?.role;
    return role ? ROLE_LABEL[role] : null;
  });

  readonly pinLabel = computed<TLabel>(() => (this.pinned() ? 'nav.action.unpin' : 'nav.action.pin'));

  onMouseEnter(): void {
    this.isHover.set(true);
  }

  onMouseLeave(): void {
    this.isHover.set(false);
  }

  onTogglePin(): void {
    this.togglePin.emit();
  }

  openProfile(): void {
    this.drawer.open(ProfileForm, {
      title: 'profile.title',
      size: 'md',
    });
  }

  openTenant(): void {
    this.drawer.open(TenantForm, {
      title: 'tenant.title',
      size: 'md',
    });
  }

  logout(): void {
    this.authStore.logout().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }
}
